import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Calendar, MapPin } from "lucide-react";
import { formatDate, formatTime } from "../lib/utils-format";

/**
 * EventCard - square 1:1 aspect ratio, homogeneous sizing.
 * Image always fills aspect-square; fixed-height footer with ellipsis on title.
 */
export const EventCard = ({ event, className = "" }) => {
    const { t, i18n } = useTranslation();
    const city = event?.venue?.city?.name || "";
    const country = event?.venue?.country?.name || event?.country?.name || "";
    const locale =
        i18n.language === "fr" ? "fr-FR" : i18n.language === "ht" ? "fr-FR" : "en-US";

    return (
        <Link
            to={`/events/${event.id}`}
            data-testid={`event-card-${event.id}`}
            className={`event-card ${className}`}
        >
            <div className="event-card-image-wrap">
                {event.featured_image_url ? (
                    <img
                        src={event.featured_image_url}
                        alt={event.title}
                        className="event-card-image"
                        loading="lazy"
                    />
                ) : (
                    <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-muted to-secondary">
                        <Calendar className="h-10 w-10 text-muted-foreground" />
                    </div>
                )}
            </div>
            <div className="event-card-content">
                <h3
                    data-testid={`event-card-title-${event.id}`}
                    className="font-heading font-bold text-base sm:text-lg leading-tight line-clamp-2 min-h-[2.5rem]"
                >
                    {event.title}
                </h3>
                <div className="flex flex-col gap-1 text-xs text-muted-foreground font-medium">
                    <span className="flex items-center gap-1.5">
                        <Calendar className="h-3.5 w-3.5 flex-shrink-0" />
                        <span className="truncate">
                            {event.start_datetime
                                ? `${formatDate(event.start_datetime, locale)} · ${formatTime(event.start_datetime, locale)}`
                                : t("event_card.tbd")}
                        </span>
                    </span>
                    <span className="flex items-center gap-1.5">
                        <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                        <span className="truncate">
                            {city || "-"}
                            {country ? `, ${country}` : ""}
                        </span>
                    </span>
                </div>
            </div>
        </Link>
    );
};

export const EventCardGrid = ({ events = [] }) => {
    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 md:gap-6">
            {events.map((ev) => (
                <EventCard key={ev.id} event={ev} />
            ))}
        </div>
    );
};
